/**
 * V2 Select — native `<select>` styled to V2 tokens, faithful to
 * `select.inp` in design_reference/styles/app.css (custom chevron,
 * sunken well, accent focus ring).
 */

import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';

/** Inline SVG chevron used as the native select indicator. */
const CHEVRON_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%237d8799' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")";

/** Props for the V2 Select (native select attributes). */
export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

/** V2 native select. Pass `<option>` children. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, style, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'w-full h-[34px] pl-[11px] pr-[30px] rounded-[var(--r-md)] appearance-none bg-no-repeat',
        'bg-[var(--bg-inset)] border border-[var(--bd)] text-[var(--tx-hi)] text-[13px]',
        'shadow-[inset_0_1px_1px_rgba(0,0,0,.15)] outline-none',
        'focus:border-[var(--accent-line)] focus:shadow-[0_0_0_3px_var(--accent-soft)]',
        'disabled:opacity-50 disabled:pointer-events-none',
        className,
      )}
      style={{
        backgroundImage: CHEVRON_BG,
        backgroundPosition: 'right 9px center',
        ...style,
      }}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
